import { Redirect, Route } from "react-router-dom";
import { IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { QueryClientConfig } from "@tanstack/react-query";
import { App as CapacitorApp } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { AuthProvider, useAuthStore } from "@/hooks/useAuth";
import { handlePendingInvitation } from "@/hooks/usePendingInvitation";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "@ionic/react/css/palettes/dark.class.css";
/* Theme variables */
import "./theme/variables.css";
import "./theme/global.css";
import "./theme/custom.css";
import "./theme/themes.css";

/* Pages */
import {
  Welcome,
  VerifyEmail,
  Onboarding,
  EnterCode,
  EnterPassword,
  Organisations,
} from "./pages/auth";

/* Components */
import PrivateRoute from "./components/PrivateRoute";
import TabLayout from "./components/TabLayout";
import React, { useEffect } from "react";
import { Switch } from "react-router";
import { useDarkMode, useTheme } from "@/hooks";
import "./services/Sentry";
import { report } from "@services/Sentry.ts";

setupIonicReact({
  mode: "ios",
  swipeBackEnabled: true,
});

// Configure React Query
const queryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
};

const queryClient = new QueryClient(queryConfig);

const App: React.FC = () => {
  useDarkMode();
  useTheme();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // When auth becomes ready (app restart while already logged in + pending deep link)
  useEffect(() => {
    if (!isAuthenticated) return;
    handlePendingInvitation(queryClient).catch((e) => report(e));
  }, [isAuthenticated]);

  useEffect(() => {
    const handle = CapacitorApp.addListener("appUrlOpen", async (event) => {
      try {
        const url = new URL(event.url);
        const match = url.pathname.match(/\/invitations\/([^/]+)\/open/);
        if (!match) return;

        const invitationId = match[1];
        await Preferences.set({
          key: "pending_invitation_id",
          value: invitationId,
        });

        if (useAuthStore.getState().isAuthenticated) {
          await handlePendingInvitation(queryClient);
        }
      } catch (e) {
        report(e, {
          appUrlOpen: event.url,
        });
      }
    });

    return () => {
      handle.then((l) => l.remove());
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <IonApp>
          <IonReactRouter>
            <IonRouterOutlet>
              <Switch>
                {/* Auth flow routes */}
                <Route exact path="/auth/welcome" component={Welcome} />
                <Route
                  exact
                  path="/auth/verify-email"
                  component={VerifyEmail}
                />
                <Route exact path="/auth/onboarding" component={Onboarding} />
                <Route exact path="/auth/enter-code" component={EnterCode} />
                <Route
                  exact
                  path="/auth/enter-password"
                  component={EnterPassword}
                />
                <Route
                  exact
                  path="/auth/organisations"
                  component={Organisations}
                />

                {/* Legacy login route - redirect to new flow */}
                <Route
                  exact
                  path="/login"
                  render={() => <Redirect to="/auth/welcome" />}
                />

                {/* Protected routes */}
                <PrivateRoute
                  exact
                  path="/"
                  component={() => <Redirect to="/tabs/home" />}
                />
                <PrivateRoute path="/tabs" component={TabLayout} />
              </Switch>
            </IonRouterOutlet>
          </IonReactRouter>
        </IonApp>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
